import { environment } from 'src/environments/environment';
import { OTLPConnection } from '@overa/shared';
import { dynamic_registry } from './utils/dynamic_registry';
import { LOGGER_DEFAULTS } from './logger.config';
import { applyPlatformSession, sessionPlatform } from './core/platform/platform-session';

/**
 * Backoffice application configuration.
 * Keys from AppSettings (served at /appSettings by Backoffice.UI) are
 * available in `appSettingsData` at runtime.
 */
export const AppConfig = {
  appName: environment.app,
  appVersion: environment.version,

  customConfig: (configData: { [key: string]: any }, appSettingsData: any) => {
    // ── Ensure module sections exist (offline fallback provides bare object) ──
    configData['overa-shared']     = configData['overa-shared']     || {};
    configData['overa-security']   = configData['overa-security']   || {};
    configData['overa-dynamic']    = configData['overa-dynamic']    || {};
    configData['overa-components'] = configData['overa-components'] || {};

    // ── Registry de componentes dinámicos (siempre necesario) ──────────────
    configData['overa-dynamic']['registry'] = dynamic_registry;

    // ── Static defaults ────────────────────────────────────────────────
    configData['overa-shared'].defaultTheme = 'theme-admin';
    configData['overa-shared'].useGoldenLayout = false;
    configData['overa-shared'].otlpConnection = new OTLPConnection({ url: '' });

    // Logging. Los valores por defecto los aplica ya `main.ts` antes del
    // arranque; aquí se vuelven a aplicar mezclados con lo que mande el
    // servidor, de forma que se pueda subir el nivel de traza de un entorno ya
    // desplegado sin recompilar. Ejemplo de appSettings:
    //   "logger": { "level": "debug", "scopes": { "DynamicStudio": "trace" } }
    configData['overa-shared'].logger = {
      ...LOGGER_DEFAULTS,
      ...(appSettingsData?.logger ?? {}),
      scopes: {
        ...(LOGGER_DEFAULTS.scopes ?? {}),
        ...(appSettingsData?.logger?.scopes ?? {}),
      },
    };

    // Posición de aparición de los mensajes toast (parámetro global).
    // Valores posibles: 'top-right', 'top-left', 'bottom-right', 'bottom-left',
    // 'top-center', 'bottom-center', 'center-center', 'top-full-width',
    // 'bottom-full-width'.
    configData['overa-shared'].messagesPosition = appSettingsData?.messagesPosition || 'bottom-right';

    // Asistente general de IA (sección `AiChat` del appsettings de la UI): agente, textos, chips…
    // Ver LayoutComponent.aiChat y AiChatSettings.cs.
    configData['overa-components']['aiChatMicEnabled'] = appSettingsData?.aiChat?.micEnabled ?? false;
    configData['overa-components']['aiChat'] = appSettingsData?.aiChat ?? {};

    configData['overa-dynamic'].dynamicListParentRoute = 'admin/dynamic-view';
    configData['overa-dynamic'].dynamicView_adminCanEdit = true;
    configData['overa-dynamic'].dynamicView_adminCanDelete = true;
    configData['overa-dynamic'].formEditorRoles = configData['overa-dynamic'].formEditorRoles || [];

    configData['overa-security'].clientId = 'backoffice';
    configData['overa-security'].silent_redirect_uri = '';
    configData['overa-security'].sessionChecker = false;
    configData['overa-security'].showDebug = false;

    // ── Dynamic config (provided by the .NET SPA host) ─────────────────
    if (appSettingsData) {
      // Sesión de consola o de plataforma (/p/{código}, IdP de esa plataforma). Antes de que la librería
      // cargue el descubrimiento, que sale de appSettingsData.identityApiUrl. Ver platform-session.ts.
      applyPlatformSession(configData, appSettingsData);

      // Casilla «Administrador global» del formulario de usuario (@overa/security): solo en la consola. Quién
      // puede usarla lo decide Identity (raíz, u operador con platforms.admins); un admin de plataforma, nunca.
      configData['overa-security'].globalAdminToggle = !sessionPlatform();

      // The BFF URL — Angular uses this to prefix API calls
      configData['overa-dynamic'] = configData['overa-dynamic'] || {};
      // Backoffice routes API calls through its own proxy (/api/{apiName})
      configData['overa-dynamic'].useBackofficeProxy = true;
      // 'baseApi' is required by the @overa/base framework (setConfigValues registers it as the
      // first configApisDynamic entry). It resolves the [baseApi] token in dynamic view scripts
      // and endpoints — those views are authored against the Activity API, so this entry points
      // there. Note: overa-security.apiBaseUrl is set independently from data.baseApiUrl
      // (AppSettings.BaseApiUrl → backofficeApiUrl) and is NOT affected by this entry.
      // 'baseApi' is filtered out of all microservice dropdowns so it never appears in the UI.
      // Solo lo que es del propio BackOffice. Los productos y servicios (activityApi, harleyApi,
      // calendarApi…) ya NO salen de appsettings: los gobierna el registro de plataformas y
      // `PlatformService.registerTargets` los da de alta al cargar `/api/platforms`, con la URL del
      // proxy del BFF. `baseApi` arranca vacío y lo apunta `PlatformService.setActive` a la API propia
      // de la plataforma abierta.
      // Notificaciones (toast, campana y el hub de SignalR): por el proxy de servicios comunes de la API del BackOffice
      // (ServiceProxy:Services:notification-api), como hacen las apps (`/services/<código>/v1`). Sin esto el hub no conecta
      // y no llegan, p. ej., los avisos de fin del alta de apps (Jobs).
      const backofficeRoot = (appSettingsData.backofficeApiUrl || '').replace(/\/+$/, '').replace(/\/v\d+$/, '');
      configData['overa-notification'] = {
        ...(configData['overa-notification'] ?? {}),
        notificationBaseUrl: `${backofficeRoot}/services/notification-api/v1`,
      };

      configData['overa-dynamic'].configApisDynamic = [
        { name: 'backofficeApi',   url: appSettingsData.backofficeApiUrl  || '' },
        { name: 'baseApi',         url: '' },
      ];
    }
  },
};
